import React, { useMemo, useState } from 'react';
import { useTeam } from '../../context/TeamContext';
import { useBrreg } from '../../context/BrregContext';
import SafeIcon from '../../common/SafeIcon';
import ExportWizard from './ExportWizard';
import ImportProspectsModal from '../outreach/ImportProspectsModal';
import { downloadBrregCsv } from '../../utils/brregExport';

const isValidUuid = value => (
  typeof value === 'string' &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)
);

const getDuplicateReason = lead => (
  String(
    lead?.duplicateReason ||
    lead?.duplicate_reason ||
    lead?.reason ||
    lead?.block_reason ||
    lead?.blockReason ||
    ''
  ).trim()
);

const isCrmDuplicateLead = lead => {
  const reason = getDuplicateReason(lead).toLowerCase();

  return (
    Boolean(lead?.crmDuplicate || lead?.crm_duplicate || lead?.isCrmDuplicate) ||
    reason.includes('crm') ||
    reason.includes('finnes allerede')
  );
};

const isDuplicateLead = lead => (
  lead?.wash_status === 'Duplikat' ||
  String(lead?.status || '').toLowerCase() === 'duplicate' ||
  isCrmDuplicateLead(lead)
);

const statusClass = status => {
  switch (status) {
    case 'Godkjent':
      return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    case 'Avvist':
      return 'bg-rose-50 text-rose-700 border-rose-200';
    case 'Duplikat':
      return 'bg-orange-50 text-orange-700 border-orange-200';
    case 'Feil':
      return 'bg-red-50 text-red-700 border-red-200';
    case 'Eksportert':
      return 'bg-sky-50 text-sky-700 border-sky-200';
    case 'Til vurdering':
      return 'bg-amber-50 text-amber-700 border-amber-200';
    default:
      return 'bg-slate-50 text-slate-600 border-slate-200';
  }
};

const approvalStatuses = new Set([
  'Ny',
  'Vasket',
  'Mangler kontaktinfo',
  'Mangler nettside',
  'Under minimumsscore'
]);

const StagingBoard = ({ jobId }) => {
  const { users } = useTeam();
  const {
    stagingLeads,
    washLeads,
    updateLeadStatus,
    approveLeads,
    distributeLeads
  } = useBrreg();
  const [showWizard, setShowWizard] = useState(false);
  const [showLinkedInImport, setShowLinkedInImport] = useState(false);
  const [selectedLinkedInLead, setSelectedLinkedInLead] = useState(null);
  const [downloadMessage, setDownloadMessage] = useState('');
  const [copyMessage, setCopyMessage] = useState('');
  const [copyFallbackUrl, setCopyFallbackUrl] = useState('');
  const [distributionMessage, setDistributionMessage] = useState(null);
  const [statusMessage, setStatusMessage] = useState(null);
  const [distributionConfig, setDistributionConfig] = useState({
    method: 'round-robin',
    ownerId: ''
  });
  
  // Visuelt state for vaskeregler (For å vise funksjonaliteten)
  const [washConfig, setWashConfig] = useState({
    preventDuplicates: true,
    requireContactInfo: false,
    requireWebsite: false,
    minQualityScore: 30
  });

  const availableOwners = useMemo(() => (
    (Array.isArray(users) ? users : [])
      .map(user => ({
        id: user.user_id || user.id,
        name: user.name || user.full_name || user.email || 'Ukjent bruker',
        email: user.email || ''
      }))
      .filter(owner => isValidUuid(owner.id))
  ), [users]);

  const ownerNameById = useMemo(
    () => new Map(availableOwners.map(owner => [owner.id, owner.name])),
    [availableOwners]
  );

  const lanes = ['Ny', 'Vasket', 'Til vurdering', 'Godkjent', 'Avvist', 'Duplikat', 'Feil', 'Mangler kontaktinfo', 'Mangler nettside', 'Under minimumsscore', 'Eksportert'];
  const safeStagingLeads = Array.isArray(stagingLeads) ? stagingLeads : [];
  const selectedJobId = jobId;
  const filteredJobLeads = selectedJobId
    ? safeStagingLeads.filter(
        lead => String(lead.job_id || lead.jobId || lead.importJobId || '') === String(selectedJobId)
      )
    : safeStagingLeads;
  const getLeadsByStatus = (status) => filteredJobLeads.filter(l => l.wash_status === status);
  const currentJobLeads = filteredJobLeads;
  const approvedWithoutOwner = currentJobLeads.filter(
    lead => lead.wash_status === 'Godkjent' && !isValidUuid(lead.suggested_owner_id)
  ).length;
  const assignedCount = currentJobLeads.filter(
    lead => isValidUuid(lead.suggested_owner_id)
  ).length;

  const handleProcess = async () => {
    if (!jobId) return;
    await washLeads(jobId, washConfig);
  };

  const handleStatusChange = async (lead, nextStatus) => {
    if (!lead?.id || !nextStatus || isDuplicateLead(lead)) return;

    setStatusMessage(null);
    const updated = await updateLeadStatus(lead.id, nextStatus);

    setStatusMessage(updated
      ? {
          type: 'info',
          text: `Lead satt til ${nextStatus}.`
        }
      : {
          type: 'error',
          text: 'Kunne ikke oppdatere status for leadet.'
        });
  };

  const handleManualApprove = async lead => {
    if (!lead?.id || isDuplicateLead(lead) || !approvalStatuses.has(lead.wash_status)) {
      return;
    }

    await updateLeadStatus(lead.id, 'Godkjent');
  };

  const handleDistribute = async () => {
    setDistributionMessage(null);

    const result = await distributeLeads({
      method: distributionConfig.method,
      ownerId: isValidUuid(distributionConfig.ownerId)
        ? distributionConfig.ownerId
        : '',
      owners: availableOwners,
      jobId
    });

    const succeeded = result === true || result?.success === true;

    if (succeeded) {
      const message = result?.message
        || (result?.assigned === 0 && result?.unassigned > 0
          ? 'Ingen gyldige eiere valgt – leads beholdes uten eier.'
          : 'Leads ble fordelt uten feil.');

      setDistributionMessage({
        type: 'info',
        text: message
      });
      return;
    }

    setDistributionMessage({
      type: 'error',
      text: result?.message || 'Kunne ikke fordele leads. Ingen endringer ble gjort.'
    });
  };

  const handleDownloadCsv = () => {
    const downloadableLeads = filteredJobLeads.filter(
      lead => lead.wash_status === 'Godkjent' && !isDuplicateLead(lead)
    );

    if (downloadableLeads.length === 0) {
      setDownloadMessage('Ingen godkjente leads å laste ned.');
      return;
    }

    downloadBrregCsv(downloadableLeads);
    setDownloadMessage('Liste lastet ned');
  };

  const getLinkedInPeopleSearchUrl = (lead) => {
    const companyName = String(lead.name || '').trim();

    if (!companyName) return '';

    return `https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent(companyName)}`;
  };

  const handleCopyLinkedInSearch = async (lead) => {
    const searchUrl = getLinkedInPeopleSearchUrl(lead);

    if (!searchUrl) return;

    setCopyMessage('');
    setCopyFallbackUrl('');

    try {
      if (!navigator.clipboard || typeof navigator.clipboard.writeText !== 'function') {
        throw new Error('Clipboard API unavailable');
      }

      await navigator.clipboard.writeText(searchUrl);
      setCopyMessage('LinkedIn-søk kopiert');
    } catch {
      setCopyMessage('Kunne ikke kopiere automatisk. Kopier lenken manuelt:');
      setCopyFallbackUrl(searchUrl);
    }
  };

  const handleOpenLinkedInImport = (lead) => {
    setSelectedLinkedInLead(lead);
    setShowLinkedInImport(true);
  };

  const handleCloseLinkedInImport = () => {
    setShowLinkedInImport(false);
    setSelectedLinkedInLead(null);
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {showWizard && <ExportWizard onClose={() => setShowWizard(false)} />}

      {showLinkedInImport && selectedLinkedInLead && (
        <ImportProspectsModal
          isOpen={showLinkedInImport}
          onClose={handleCloseLinkedInImport}
          initialCompanyName={selectedLinkedInLead.name || ''}
          sourceNote={`Importert fra BRREG-lead: ${selectedLinkedInLead.name || ''} / orgnr ${selectedLinkedInLead.orgnr || ''}`}
        />
      )}

      <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 text-sm text-blue-800">
        <div className="flex items-start gap-3">
          <SafeIcon icon="ℹ️" className="w-5 h-5 flex-shrink-0" />
          <div>
            <p className="font-bold mb-1">BRREG → LinkedIn Outreach</p>
            <p>
              LinkedIn brukes manuelt. Systemet henter ikke data fra LinkedIn, sender ikke meldinger og åpner ikke profiler automatisk. Du limer selv inn profiler du har lovlig funnet.
            </p>
          </div>
        </div>
      </div>

      {/* Vaskeregler / Innstillinger Rammeverk - Alltid synlig */}
      <div className="bg-white p-6 md:p-8 rounded-[2rem] border border-slate-200 shadow-sm flex flex-col lg:flex-row gap-8 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-40 h-40 bg-indigo-50 rounded-bl-full -mr-10 -mt-10 z-0 pointer-events-none"></div>
        
        <div className="lg:w-1/3 relative z-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-indigo-50 text-indigo-700 rounded-full text-[10px] font-black uppercase tracking-widest mb-3 border border-indigo-100">
            <SafeIcon icon="🧽" className="w-3 h-3" />
            Datavask
          </div>
          <h3 className="text-xl font-black text-slate-900 tracking-tight mb-2">
            Vaskeregler & Kriterier
          </h3>
          <p className="text-sm text-slate-500 font-medium leading-relaxed">
            Definer hvordan innkommende leads skal filtreres, vaskes og berikes før de godkjennes for eksport til CRM.
          </p>
        </div>

        <div className="lg:w-2/3 grid grid-cols-1 sm:grid-cols-2 gap-4 relative z-10">
          <label className={`flex items-start gap-4 p-4 border-2 rounded-xl cursor-pointer transition-all ${washConfig.preventDuplicates ? 'border-primary-500 bg-primary-50/50' : 'border-slate-100 hover:border-slate-200 bg-white'}`}>
            <input 
              type="checkbox" 
              checked={washConfig.preventDuplicates} 
              onChange={e => setWashConfig({...washConfig, preventDuplicates: e.target.checked})} 
              className="mt-1 w-5 h-5 text-primary-600 rounded border-slate-300 focus:ring-primary-500" 
            />
            <div>
              <span className="block text-sm font-bold text-slate-900">Fjern duplikater</span>
              <span className="block text-xs text-slate-500 mt-1">Sjekk automatisk mot eksisterende org.nr i CRM for å unngå dobbeltregistrering.</span>
            </div>
          </label>

          <label className={`flex items-start gap-4 p-4 border-2 rounded-xl cursor-pointer transition-all ${washConfig.requireContactInfo ? 'border-primary-500 bg-primary-50/50' : 'border-slate-100 hover:border-slate-200 bg-white'}`}>
            <input 
              type="checkbox" 
              checked={washConfig.requireContactInfo} 
              onChange={e => setWashConfig({...washConfig, requireContactInfo: e.target.checked})} 
              className="mt-1 w-5 h-5 text-primary-600 rounded border-slate-300 focus:ring-primary-500" 
            />
            <div>
              <span className="block text-sm font-bold text-slate-900">Krev e-post eller telefon</span>
              <span className="block text-xs text-slate-500 mt-1">Leads uten e-post og telefon blir ikke godkjent.</span>
            </div>
          </label>

          <div className="sm:col-span-2 p-5 border-2 border-slate-100 rounded-xl bg-white">
            <div className="flex justify-between items-end mb-4">
              <div>
                <span className="block text-sm font-bold text-slate-900">Minimum Kvalitetsscore</span>
                <span className="block text-xs text-slate-500 mt-1">Leads under denne scoren vil kreve manuell godkjenning.</span>
              </div>
              <span className="text-2xl font-black text-primary-600">{washConfig.minQualityScore}</span>
            </div>
            <input 
              type="range" 
              min="0" 
              max="100" 
              step="10" 
              value={washConfig.minQualityScore} 
              onChange={e => setWashConfig({...washConfig, minQualityScore: Number(e.target.value)})} 
              className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-primary-600" 
            />
            <div className="flex justify-between mt-2 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
              <span>0 (Alt godtas)</span>
              <span>100 (Kun perfekt data)</span>
            </div>
          </div>

          <label className={`sm:col-span-2 flex items-start gap-4 p-4 border-2 rounded-xl cursor-pointer transition-all ${washConfig.requireWebsite ? 'border-primary-500 bg-primary-50/50' : 'border-slate-100 hover:border-slate-200 bg-white'}`}>
            <input
              type="checkbox"
              checked={washConfig.requireWebsite}
              onChange={e => setWashConfig({ ...washConfig, requireWebsite: e.target.checked })}
              className="mt-1 w-5 h-5 text-primary-600 rounded border-slate-300 focus:ring-primary-500"
            />
            <div>
              <span className="block text-sm font-bold text-slate-900">Krev registrert nettside</span>
              <span className="block text-xs text-slate-500 mt-1">Leads uten nettside blir ikke godkjent.</span>
            </div>
          </label>
        </div>
      </div>

      {/* Distribusjon */}
      <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-end gap-4">
          <div className="flex-1">
            <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest">Distribusjon</h3>
            <p className="text-xs text-slate-500 mt-1">Fordel kun godkjente leads som ikke allerede har ansvarlig.</p>
          </div>

          <label className="text-xs font-bold text-slate-600">
            Metode
            <select
              value={distributionConfig.method}
              onChange={e => setDistributionConfig({
                ...distributionConfig,
                method: e.target.value,
                ownerId: ''
              })}
              className="block mt-1 w-full lg:w-48 px-3 py-2 rounded-lg border border-slate-200 text-sm text-slate-700"
            >
              <option value="round-robin">Round Robin</option>
              <option value="single">Én ansvarlig</option>
            </select>
          </label>

          {distributionConfig.method === 'single' && (
            <label className="text-xs font-bold text-slate-600">
              Velg ansvarlig
              <select
                value={distributionConfig.ownerId}
                onChange={e => setDistributionConfig({
                  ...distributionConfig,
                  ownerId: e.target.value
                })}
                className="block mt-1 w-full lg:w-56 px-3 py-2 rounded-lg border border-slate-200 text-sm text-slate-700"
              >
                <option value="">Velg ansvarlig</option>
                {availableOwners.map(owner => (
                  <option key={owner.id} value={owner.id}>{owner.name}</option>
                ))}
              </select>
            </label>
          )}

          {distributionConfig.method === 'round-robin' && (
            <div className="text-xs text-slate-500">
              <span className="font-bold text-slate-700">Eiere i fordelingen:</span>{' '}
              {availableOwners.length > 0
                ? availableOwners.map(owner => owner.name).join(', ')
                : 'Ingen team-brukere tilgjengelig'}
            </div>
          )}

          <button
            onClick={handleDistribute}
            disabled={!jobId}
            className="px-5 py-2.5 bg-purple-50 text-purple-700 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-purple-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Fordel godkjente leads
          </button>
        </div>

        {distributionMessage && (
          <p
            className={`mt-3 text-xs font-bold ${
              distributionMessage.type === 'error'
                ? 'text-red-700'
                : 'text-amber-700'
            }`}
            role={distributionMessage.type === 'error' ? 'alert' : 'status'}
          >
            {distributionMessage.text}
          </p>
        )}

        {availableOwners.length === 0 && (
          <p className="mt-3 text-xs font-bold text-slate-500">
            Ingen eier valgt. Leads kan fortsatt eksporteres uten eier.
          </p>
        )}

        <div className="flex flex-wrap gap-4 mt-4 text-xs font-bold text-slate-500">
          <span>{approvedWithoutOwner} godkjente leads uten ansvarlig</span>
          <span>{assignedCount} fordelt</span>
        </div>
      </div>

      {/* Last ned liste */}
      <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="flex-1">
            <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest">Last ned liste</h3>
            <p className="text-xs text-slate-500 mt-1">
              Laster ned BRREG-leads fra valgt importjobb. Dette påvirker ikke CRM.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={handleDownloadCsv}
              disabled={!jobId}
              className="px-5 py-2.5 bg-slate-100 text-slate-700 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-slate-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Last ned CSV
            </button>
            <button
              type="button"
              disabled
              className="px-5 py-2.5 bg-slate-50 text-slate-400 rounded-xl text-xs font-black uppercase tracking-widest border border-slate-200 disabled:cursor-not-allowed"
            >
              Excel kommer senere
            </button>
          </div>
        </div>

        {downloadMessage && (
          <p className="mt-3 text-xs font-bold text-slate-600" role="status">
            {downloadMessage}
          </p>
        )}
      </div>

      {/* Action Bar */}
      <div className="flex flex-col sm:flex-row justify-between items-center bg-white p-4 rounded-2xl border border-slate-200 shadow-sm gap-4">
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          <button 
            onClick={handleProcess} 
            disabled={!jobId}
            className="flex-1 sm:flex-none px-6 py-3 bg-indigo-50 text-indigo-700 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-indigo-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <SafeIcon icon="🧽" className="w-4 h-4" />
            1. Vask & Godkjenn
          </button>
        </div>
        <button 
          onClick={() => setShowWizard(true)} 
          disabled={!jobId}
          className="w-full sm:w-auto px-8 py-3 bg-emerald-600 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-emerald-700 shadow-lg shadow-emerald-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          <SafeIcon icon="🚀" className="w-4 h-4" />
          3. Eksport Wizard
        </button>
      </div>

      {/* Kanban Board Container */}
      <div className="relative bg-slate-50/50 rounded-[2rem] border border-slate-200 p-6 overflow-hidden">
        {!jobId && (
          <div className="absolute inset-0 z-20 bg-white/60 backdrop-blur-sm flex flex-col items-center justify-center rounded-[2rem] p-6 text-center">
            <div className="w-20 h-20 bg-white shadow-lg rounded-2xl flex items-center justify-center mb-6 border border-slate-100 animate-bounce-slow">
              <SafeIcon icon="📥" className="w-10 h-10 text-slate-300" />
            </div>
            <h3 className="text-2xl font-black text-slate-900 tracking-tight mb-2">Ingen importjobb valgt</h3>
            <p className="text-slate-500 font-medium max-w-md mx-auto">
              Rammeverket er klart. Gå til <span className="font-bold text-slate-700">Import & Søk</span>-fanen for å hente inn nye leads, eller velg en tidligere jobb fra historikken.
            </p>
          </div>
        )}

        <div className="flex gap-6 overflow-x-auto pb-4 min-h-[500px] custom-scrollbar relative z-10">
          {lanes.map(lane => (
            <div key={lane} className="min-w-[300px] w-[300px] flex flex-col bg-slate-100/80 rounded-2xl p-4 border border-slate-200/60">
              <div className="flex justify-between items-center mb-4 px-1">
                <h3 className="font-black text-slate-800 text-sm uppercase tracking-widest">{lane}</h3>
                <span className="bg-white px-2.5 py-1 rounded-lg text-[10px] font-black text-slate-500 shadow-sm border border-slate-200">
                  {getLeadsByStatus(lane).length}
                </span>
              </div>
              
              <div className="flex-1 space-y-3 overflow-y-auto custom-scrollbar pr-1">
                {getLeadsByStatus(lane).map(lead => {
                  const linkedinPeopleSearchUrl = getLinkedInPeopleSearchUrl(lead);
                  const duplicate = isDuplicateLead(lead);
                  const crmDuplicate = isCrmDuplicateLead(lead);
                  const duplicateReason = getDuplicateReason(lead);
                  const canManuallyApprove = approvalStatuses.has(lead.wash_status);
                  const ownerId = isValidUuid(lead.suggested_owner_id)
                    ? lead.suggested_owner_id
                    : null;

                  return (
                    <div key={lead.id} className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm hover:shadow-md hover:border-blue-300 transition-all group">
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="font-bold text-slate-900 text-sm line-clamp-1 group-hover:text-blue-700 transition-colors" title={lead.name}>
                          {lead.name}
                        </h4>
                        <span className={`text-[10px] font-black px-2 py-0.5 rounded-md shrink-0 border ${lead.data_quality_score > 30 ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-amber-50 text-amber-700 border-amber-100'}`}>
                          {lead.data_quality_score}
                        </span>
                      </div>
                      <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest truncate">
                        {lead.naeringskode_desc}
                      </p>
                      
                      <div className="mt-3 pt-3 border-t border-slate-50 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Status</span>
                          <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-md border ${statusClass(duplicate ? 'Duplikat' : lead.wash_status)}`}>
                            {duplicate ? 'Duplikat' : lead.wash_status || 'Ny'}
                          </span>
                        </div>

                        {crmDuplicate && (
                          <p className="rounded-lg border border-orange-200 bg-orange-50 px-3 py-2 text-[10px] font-black uppercase tracking-widest text-orange-700">
                            Finnes allerede i CRM
                          </p>
                        )}

                        {duplicate && !crmDuplicate && duplicateReason && (
                          <p className="rounded-lg border border-orange-200 bg-orange-50 px-3 py-2 text-[10px] font-bold text-orange-700">
                            {duplicateReason}
                          </p>
                        )}

                        {canManuallyApprove && !duplicate && (
                          <button
                            type="button"
                            onClick={() => handleManualApprove(lead)}
                            className="w-full rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-[10px] font-black uppercase tracking-widest text-emerald-700 hover:bg-emerald-100 transition-colors"
                          >
                            Godkjenn manuelt
                          </button>
                        )}

                        {lead.wash_status === 'Til vurdering' && !duplicate && (
                          <div className="space-y-2">
                            <button
                              type="button"
                              onClick={() => handleManualApprove(lead)}
                              className="w-full rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-[10px] font-black uppercase tracking-widest text-emerald-700 hover:bg-emerald-100 transition-colors"
                            >
                              Godkjenn
                            </button>
                            <button
                              type="button"
                              onClick={() => handleStatusChange(lead, 'Avvist')}
                              className="w-full rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[10px] font-black uppercase tracking-widest text-rose-700 hover:bg-rose-100 transition-colors"
                            >
                              Avvis
                            </button>
                            <button
                              type="button"
                              onClick={() => handleStatusChange(lead, 'Ny')}
                              className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[10px] font-black uppercase tracking-widest text-slate-600 hover:bg-slate-100 transition-colors"
                            >
                              Sett tilbake til Ny
                            </button>
                          </div>
                        )}

                        {lead.wash_status === 'Godkjent' && !duplicate && (
                          <div className="space-y-2">
                            <button
                              type="button"
                              onClick={() => handleStatusChange(lead, 'Til vurdering')}
                              className="w-full rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[10px] font-black uppercase tracking-widest text-amber-700 hover:bg-amber-100 transition-colors"
                            >
                              Sett til vurdering
                            </button>
                            <button
                              type="button"
                              onClick={() => handleStatusChange(lead, 'Avvist')}
                              className="w-full rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[10px] font-black uppercase tracking-widest text-rose-700 hover:bg-rose-100 transition-colors"
                            >
                              Avvis
                            </button>
                          </div>
                        )}

                        {lead.wash_status === 'Avvist' && !duplicate && (
                          <button
                            type="button"
                            onClick={() => handleStatusChange(lead, 'Til vurdering')}
                            className="w-full rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[10px] font-black uppercase tracking-widest text-amber-700 hover:bg-amber-100 transition-colors"
                          >
                            Gjenåpne til vurdering
                          </button>
                        )}

                        {lead.wash_status === 'Duplikat' && (
                          <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-center text-[10px] font-black uppercase tracking-widest text-slate-600">
                            Ikke tilgjengelig for godkjenning eller eksport
                          </p>
                        )}

                        {lead.wash_status === 'Feil' && !duplicate && (
                          <div className="space-y-2">
                            <button
                              type="button"
                              onClick={() => handleStatusChange(lead, 'Til vurdering')}
                              className="w-full rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[10px] font-black uppercase tracking-widest text-amber-700 hover:bg-amber-100 transition-colors"
                            >
                              Sett til vurdering
                            </button>
                            <button
                              type="button"
                              onClick={() => handleStatusChange(lead, 'Avvist')}
                              className="w-full rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[10px] font-black uppercase tracking-widest text-rose-700 hover:bg-rose-100 transition-colors"
                            >
                              Avvis
                            </button>
                          </div>
                        )}

                        {lead.wash_status === 'Eksportert' && (
                          <p className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-center text-[10px] font-black uppercase tracking-widest text-sky-700">
                            Eksportert – låst
                          </p>
                        )}

                        {lead.website && (
                          <div className="flex items-center gap-1.5 text-[10px] font-bold text-blue-600">
                            <SafeIcon icon="🌐" className="w-3 h-3" /> {lead.website.replace('https://', '').replace('http://', '')}
                          </div>
                        )}
                        {ownerId && ownerNameById.has(ownerId) ? (
                          <div className="flex items-center gap-2">
                            <div className="w-5 h-5 bg-indigo-100 rounded-md flex items-center justify-center text-[9px] font-black text-indigo-700">
                              {ownerNameById.get(ownerId).substring(0, 2).toUpperCase()}
                            </div>
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                              Ansvarlig: {ownerNameById.get(ownerId)}
                            </span>
                          </div>
                        ) : (
                          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                            Ingen eier
                          </span>
                        )}
                        {lead.wash_status === 'Godkjent' && !duplicate && (
                          <div className="pt-2 space-y-2">
                            <a
                              href={linkedinPeopleSearchUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block w-full rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-center text-[10px] font-black uppercase tracking-widest text-blue-700 hover:bg-blue-100 transition-colors"
                            >
                              Finn personer på LinkedIn
                            </a>
                            <button
                              type="button"
                              onClick={() => handleCopyLinkedInSearch(lead)}
                              className="w-full rounded-lg border border-blue-200 bg-white px-3 py-2 text-center text-[10px] font-black uppercase tracking-widest text-blue-700 hover:bg-blue-50 transition-colors"
                            >
                              Kopier LinkedIn-søk
                            </button>
                            <p className="text-[10px] leading-relaxed text-slate-500">
                              Hvis LinkedIn blokkeres i preview, kopier lenken og åpne den i ny fane.
                            </p>
                            <button
                              type="button"
                              onClick={() => handleOpenLinkedInImport(lead)}
                              className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-center text-[10px] font-black uppercase tracking-widest text-slate-700 hover:bg-slate-100 transition-colors"
                            >
                              Legg til LinkedIn-prospekter
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}

                {jobId && getLeadsByStatus(lane).length === 0 && (
                  <div className="h-24 border-2 border-dashed border-slate-200 rounded-xl flex items-center justify-center text-slate-400">
                    <span className="text-[10px] font-black uppercase tracking-widest">Tomt</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {statusMessage && (
        <div className="fixed bottom-6 left-6 z-40 max-w-md rounded-xl border border-slate-200 bg-white p-4 shadow-xl">
          <p
            className={`text-xs font-bold ${statusMessage.type === 'error' ? 'text-red-700' : 'text-slate-700'}`}
            role={statusMessage.type === 'error' ? 'alert' : 'status'}
          >
            {statusMessage.text}
          </p>
        </div>
      )}

      {(copyMessage || copyFallbackUrl) && (
        <div className="fixed bottom-6 right-6 z-40 max-w-md rounded-xl border border-slate-200 bg-white p-4 shadow-xl">
          {copyMessage && (
            <p className="text-xs font-bold text-slate-700" role="status">
              {copyMessage}
            </p>
          )}
          {copyFallbackUrl && (
            <input
              type="text"
              readOnly
              value={copyFallbackUrl}
              onFocus={event => event.target.select()}
              className="mt-2 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600"
              aria-label="LinkedIn-søkeadresse"
            />
          )}
        </div>
      )}
    </div>
  );
};

export default StagingBoard;